import { yupResolver } from '@hookform/resolvers/yup';
import {
  countryCode
} from '@payments-checkout/shared/helpers/constants/country-code.const';
import { documentTypesKeys } from '@payments-checkout/shared/helpers/constants/document-person-type.const';
import { digitalWalletInformationSchema } from '@payments-checkout/shared/helpers/constants/validation-input';
import { InstallmentsManager } from '@payments-checkout/shared/helpers/index';
import { usePaymentMethod } from '@payments-checkout/shared/providers';
import { Select, Text } from '@stone-payments/jade';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { PhoneInput } from '../../app/components/customerDataForm/phoneInput.component';
import { DocumentInput } from '../../components/documentInput/documentInput';
import { usePageSharedData } from '../../hooks/usePageSharedData';


type GooglePayRequiredFieldsProps = {
  setIsDigitalWalletValid: (value: boolean) => void;
};

type GooglePayFormFields = {
  documentType: string
  cpf?: string
  cnpj?: string
  passaport?: string
  installments?: string | null;
  countryCode: string;
  phone: string;
}

export const GooglePayRequiredFields = ({
  setIsDigitalWalletValid,
}: GooglePayRequiredFieldsProps) => {
  const { formatMessage } = useIntl();
  const {
    setDigitalWalletDocument,
    setDigitalWalletPhone,
    installments,
    setInstallments,
    setInstallmentsExplanationText,
    setSurchargesText,
  } = usePaymentMethod();

  const { installmentOptions } = usePageSharedData();

  const formattedInstallmentsOptions = InstallmentsManager.formatOptions(installmentOptions, formatMessage)

  const methods = useForm<GooglePayFormFields>({
    mode: 'onBlur',
    resolver: yupResolver(digitalWalletInformationSchema),
    defaultValues: {
      documentType: 'cpf',
      installments: installments?.toString(),
      countryCode: `${countryCode[0].value}`,
    },
  });

  const documentType = useWatch({
    control: methods.control,
    name: 'documentType',
    defaultValue: 'cpf',
  });

  const selectedCountryCode = useWatch({
    control: methods.control,
    name: 'countryCode',
  });

  const handleInstallments = (e: React.ChangeEvent<HTMLSelectElement>) => InstallmentsManager.handleInstallmentsChange(
      e.target.value,
      formattedInstallmentsOptions,
      setInstallments,
      setInstallmentsExplanationText,
      setSurchargesText
    )

  useEffect(() => {
    if (formattedInstallmentsOptions.length === 1) {
      const [onlyOption] = formattedInstallmentsOptions;
      methods.setValue('installments', onlyOption.value);
      InstallmentsManager.handleInstallmentsChange(
        onlyOption.value,
        formattedInstallmentsOptions,
        setInstallments,
        setInstallmentsExplanationText,
        setSurchargesText
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedInstallmentsOptions]);

  useEffect(() => {
    const isValid = methods.formState.isValid;
    setIsDigitalWalletValid(methods.formState.isValid);
    if (isValid) {
      setDigitalWalletDocument({
        documentType: methods.getValues('documentType'),
        document:
          methods.getValues()[
            methods.getValues('documentType') as 'cpf' | 'cnpj' | 'passaport'
          ] || null,
      });
      setDigitalWalletPhone({
        countryCode: methods.getValues('countryCode') ?? null,
        phone: methods.getValues('phone') || null,
      })
    }
    return () => setIsDigitalWalletValid(false);
  }, [methods.formState.isValid, setIsDigitalWalletValid]);

  return (
    <FormProvider {...methods}>
      <div
        className="grid grid-cols-1 gap-jade-200 w-full sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2"
        data-testid="google-pay-required-fields"
      >
        <Select
          label={formatMessage({ id: 'customerForm.document' })}
          required
          size="medium"
          options={documentTypesKeys.map((document, index) => ({
            key: `${document}-${index}`,
            value: documentTypesKeys[index],
            label: formatMessage({ id: `customerForm.${document}` }),
          }))}
          {...methods.register('documentType')}
        />
        <DocumentInput
          key={documentType}
          documentType={documentType}
          autoFocus
        />
      </div>
      <div className="grid grid-cols-1 gap-jade-200 w-full sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2">
        <Select
          label={formatMessage({ id: 'customerForm.countryCode' })}
          options={countryCode.map((country, index) => {
            return {
              key: `${country.value}-${country.label}-${index}`,
              value: `${country.value}`,
              label: country.label,
            }
          })}
          {...methods.register('countryCode')}
          size="medium"
          />
          <PhoneInput
            selectedCountryCode={selectedCountryCode ?? ''}
            disabled={false}
          />
      </div>
      <div className="grid grid-cols-1 gap-jade-200 w-full sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-1">
        <Text variant="heading-small" color="high">
          {formatMessage({ id: 'installmentsForm.title' })}
        </Text>
        <Select
          label={formatMessage({ id: 'creditCardForm.installments' })}
          required
          size="medium"
          placeholder={formatMessage({
            id: 'creditCardForm.installmentsSelect.placeholder',
          })}
          options={formattedInstallmentsOptions}
          disabled={formattedInstallmentsOptions.length === 1}
          error={!!methods.formState.errors?.installments?.message}
          supportText={
            methods.formState.errors?.installments?.message &&
              formatMessage({
                id: `creditCardFields.validate.${methods.formState.errors?.installments?.message}`,
          })}
          {...methods.register('installments', {
            onChange: (e) => handleInstallments(e)
          })}
        />
      </div>
    </FormProvider>
  );
};
